import React, { useCallback, useState } from 'react';

import { useDropzone } from 'react-dropzone';

import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Alert, Box, Card, CardActionArea, CardContent, CircularProgress, Stack, Typography } from '@mui/material';

const STOPSELL_DOCUMENT_ACCEPT =
  'application/pdf,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.ms-excel,' +
  'text/csv,' +
  'image/png,' +
  'image/jpeg,' +
  'text/plain,' +
  'text/rtf,' +
  'application/rtf';

const ALLOWED_EXTENSIONS = /\.(pdf|xlsx|xls|csv|png|jpe?g|txt|rtf)$/i;

const TYPE_ERROR_MESSAGE = 'Unsupported file type. Please upload a PDF, Excel, CSV, PNG, JPG, TXT, or RTF file.';

interface Props {
  onSelectFile: (file: File) => void;
  isPending?: boolean;
  error?: string;
}

export default function StopsellDocumentUploadZone({ onSelectFile, isPending = false, error }: Props) {
  const [typeError, setTypeError] = useState<string | null>(null);

  const handleDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      if (!acceptedFiles.length || isPending) {
        return;
      }

      const file = acceptedFiles[0];

      if (!ALLOWED_EXTENSIONS.test(file.name)) {
        setTypeError(TYPE_ERROR_MESSAGE);
        return;
      }

      setTypeError(null);
      onSelectFile(file);
    },
    [isPending, onSelectFile],
  );

  const handleDropRejected = useCallback(() => {
    setTypeError(TYPE_ERROR_MESSAGE);
  }, []);

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: STOPSELL_DOCUMENT_ACCEPT,
    disabled: isPending,
    multiple: false,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
  });

  return (
    <Stack gap={2}>
      <Card
        variant="outlined"
        sx={{
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: isDragActive ? 'primary.main' : 'divider',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
        }}
      >
        <CardActionArea {...getRootProps()}>
          <input {...getInputProps()} />
          <CardContent>
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 4, textAlign: 'center' }}>
              {isPending ? <CircularProgress size={32} /> : <UploadFileOutlinedIcon fontSize="large" color="action" />}
              <Box>
                <Typography variant="h6">
                  {isPending ? 'Processing document...' : 'Upload stopsell document'}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Drop a file here or click to browse
                </Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                  Accepted: PDF, Excel, CSV, PNG, JPG, TXT, RTF
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>

      {typeError && <Alert severity="error">{typeError}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
